import { ChevronDown, ChevronRight, Copy, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type {
  AgentFallbackBehavior,
  AgentPreviewPromptOut,
  AgentResponseTone,
} from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useAgentPreviewPrompt } from "@/features/agents/hooks/useAgents";
import type { AgentDraft } from "@/features/agents/components/AgentDraft";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agentId: string;
  draft: AgentDraft;
}

function buildOverrides(draft: AgentDraft) {
  return {
    system_prompt: draft.system_prompt,
    response_tone: (draft.response_tone ?? undefined) as
      | AgentResponseTone
      | undefined,
    response_language: draft.response_language,
    fallback_behavior: (draft.fallback_behavior ?? undefined) as
      | AgentFallbackBehavior
      | undefined,
    memory_enabled: draft.memory_enabled,
    cite_sources: draft.cite_sources,
    auto_escalate_low_confidence: draft.auto_escalate_low_confidence,
  };
}

async function copyToClipboard(text: string, label: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copiado`);
  } catch {
    toast.error("Falha ao copiar");
  }
}

export function PromptPreviewDialog({
  open,
  onOpenChange,
  agentId,
  draft,
}: Props) {
  const { t } = useTranslation("agents");
  const mut = useAgentPreviewPrompt();
  const [data, setData] = useState<AgentPreviewPromptOut | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) return;
    setData(null);
    mut
      .mutateAsync({ agentId, overrides: buildOverrides(draft) })
      .then((res) => {
        setData(res);
        // Expand all sections by default.
        setExpanded(new Set(res.sections.map((s) => s.name)));
      })
      .catch(toastApiError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function toggleSection(name: string) {
    const next = new Set(expanded);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    setExpanded(next);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t("preview.title")}</DialogTitle>
          <p className="text-sm text-muted-foreground">
            {t("preview.subtitle")}
          </p>
        </DialogHeader>

        {!data ? (
          <div className="space-y-2 py-2">
            <Skeleton className="h-6 w-1/3" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-6 w-1/4" />
            <Skeleton className="h-20 w-full" />
            <div className="flex items-center justify-center pt-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          </div>
        ) : (
          <>
            <div className="max-h-[60vh] space-y-2 overflow-y-auto">
              {data.sections.map((section) => {
                const isOpen = expanded.has(section.name);
                return (
                  <div
                    key={section.name}
                    className="rounded-md border bg-card"
                  >
                    <div className="flex items-center justify-between gap-2 px-3 py-2">
                      <button
                        type="button"
                        onClick={() => toggleSection(section.name)}
                        className="flex flex-1 items-center gap-2 text-left text-sm font-medium"
                      >
                        {isOpen ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                        {section.label}
                      </button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          copyToClipboard(section.content, section.label)
                        }
                      >
                        <Copy className="h-3 w-3" />
                        {t("preview.copySection")}
                      </Button>
                    </div>
                    {isOpen ? (
                      <pre className="max-h-72 overflow-auto border-t bg-muted/30 p-3 font-mono text-xs whitespace-pre-wrap">
                        {section.content}
                      </pre>
                    ) : null}
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end pt-2">
              <Button
                variant="outline"
                onClick={() =>
                  copyToClipboard(data.composed_prompt, "Prompt composto")
                }
              >
                <Copy className="h-3 w-3" />
                {t("preview.copyAll")}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
